// web-admin/src/pages/setup/ApprovalWorkflowPage.js
import React, { useEffect, useState, useMemo, useCallback, useRef } from "react";
import {
  Typography,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  IconButton,
  Card,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Switch,
  FormControlLabel,
  Stack,
  Box,
  Fade,
  Snackbar,
  Alert,
  InputAdornment,
  Paper
} from "@mui/material";
import Tooltip from "@mui/material/Tooltip";
import { AgGridReact } from "ag-grid-react";
import { ModuleRegistry, AllCommunityModule } from "ag-grid-community";

import {
  Search as SearchIcon,
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Refresh as RefreshIcon,
  AccountTree as WorkflowIcon
} from "@mui/icons-material";

import {
  fetchWorkflows,
  createWorkflowStep,
  updateWorkflowStep,
  deleteWorkflowStep
} from "../../services/workflowService";

import AppHeader from "../../components/AppHeader";

import "ag-grid-community/styles/ag-grid.css";
import "ag-grid-community/styles/ag-theme-quartz.css";

ModuleRegistry.registerModules([AllCommunityModule]);

const ApprovalWorkflowPage = ({ setIsLoggedIn }) => {
  const [rowData, setRowData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchText, setSearchText] = useState("");
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  
  // Feedback state
  const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "success" });

  const [form, setForm] = useState({
    workflow_code: "",
    step_number: 1,
    approver_role: "",
    is_final_step: 0,
    is_active: 1
  });

  const gridRef = useRef(null);

  const loadWorkflows = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetchWorkflows();
      setRowData(res.workflows || []);
    } catch (err) {
      console.error("fetchWorkflows error:", err);
      setSnackbar({ open: true, message: "Failed to load workflows", severity: "error" });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadWorkflows();
  }, [loadWorkflows]);

  const handleOpenAdd = useCallback(() => {
    setEditing(null);
    setForm({
      workflow_code: "MANUAL_ATTENDANCE",
      step_number: 1,
      approver_role: "manager",
      is_final_step: 0,
      is_active: 1
    });
    setOpen(true);
  }, []);

  const handleOpenEdit = useCallback((wf) => {
    setEditing(wf.id);
    setForm({
      workflow_code: wf.workflow_code,
      step_number: wf.step_number,
      approver_role: wf.approver_role,
      is_final_step: wf.is_final_step,
      is_active: wf.is_active
    });
    setOpen(true);
  }, []);

  const handleSave = useCallback(async () => {
    try {
      let res;
      if (editing) {
        res = await updateWorkflowStep(editing, form);
      } else {
        res = await createWorkflowStep(form);
      }

      if (res.success) {
        setSnackbar({ open: true, message: `Workflow step ${editing ? "updated" : "created"} successfully`, severity: "success" });
        setOpen(false);
        loadWorkflows();
      } else {
        setSnackbar({ open: true, message: res.message || "Operation failed", severity: "error" });
      }
    } catch (err) {
      setSnackbar({ open: true, message: "Server error", severity: "error" });
    }
  }, [editing, form, loadWorkflows]);

  const handleDelete = useCallback(async (id) => {
    if (window.confirm("Are you sure you want to delete this workflow step?")) {
      try {
        const res = await deleteWorkflowStep(id);
        if (res.success) {
          setSnackbar({ open: true, message: "Workflow step deleted", severity: "success" });
          loadWorkflows();
        } else {
          setSnackbar({ open: true, message: res.message || "Delete failed", severity: "error" });
        }
      } catch (err) {
        setSnackbar({ open: true, message: "Server error", severity: "error" });
      }
    }
  }, [loadWorkflows]);

  // --- Custom Cell Renderers (Premium Guidelines) ---
  const StatusBadge = useCallback((params) => {
    const isActive = params.value === 1 || params.value === true;
    const color = isActive ? "#10b981" : "#94a3b8";
    return (
      <Box sx={{ display: "flex", alignItems: "center", height: "100%" }}>
        <Box sx={{
          width: 8, height: 8, borderRadius: "50%",
          bgcolor: color, mr: 1,
          boxShadow: isActive ? `0 0 0 2px ${color}20` : "none"
        }} />
        <Typography variant="body2" sx={{ color: isActive ? "#059669" : "#64748b", fontWeight: 600, fontSize: "0.85rem" }}>
          {isActive ? "Active" : "Inactive"}
        </Typography>
      </Box>
    );
  }, []);

  const FinalStepBadge = useCallback((params) => {
    const isFinal = params.value === 1 || params.value === true;
    if (!isFinal) return <Typography sx={{ color: "#94a3b8", fontSize: "0.85rem" }}>No</Typography>;
    return (
      <Box sx={{
        display: "inline-flex", px: 1, py: 0.2, borderRadius: 1,
        bgcolor: "#fdf2f8", border: "1px solid #fce7f3",
        alignItems: "center", height: "fit-content", mt: 1.2
      }}>
        <Typography variant="caption" sx={{ color: "#db2777", fontWeight: 800, fontSize: 10 }}>
          FINAL
        </Typography>
      </Box>
    );
  }, []);

  const ActionRenderer = useCallback((params) => (
    <Stack direction="row" spacing={0.5} justifyContent="center" alignItems="center" sx={{ height: "100%" }}>
      <Tooltip title="Edit Step">
        <IconButton size="small" onClick={() => handleOpenEdit(params.data)} sx={{ color: "primary.main", "&:hover": { bgcolor: "#f5f3ff" } }}>
          <EditIcon fontSize="small" />
        </IconButton>
      </Tooltip>
      <Tooltip title="Delete Step">
        <IconButton size="small" onClick={() => handleDelete(params.data.id)} sx={{ color: "#f43f5e", "&:hover": { bgcolor: "#fff1f2" } }}>
          <DeleteIcon fontSize="small" />
        </IconButton>
      </Tooltip>
    </Stack>
  ), [handleOpenEdit, handleDelete]);

  const columnDefs = useMemo(() => [
    {
      headerName: "Workflow Code",
      field: "workflow_code",
      flex: 2,
      minWidth: 200,
      cellStyle: { fontWeight: 800, color: "#1e293b" }
    },
    {
      headerName: "Step No.",
      field: "step_number",
      width: 120,
      cellStyle: { textAlign: "center", fontWeight: 700 }
    },
    {
      headerName: "Approver Role",
      field: "approver_role",
      flex: 1,
      minWidth: 150,
      valueFormatter: (p) => p.value?.toUpperCase()
    },
    {
      headerName: "Final Step",
      field: "is_final_step",
      width: 120,
      cellRenderer: FinalStepBadge,
      cellStyle: { textAlign: "center" }
    },
    {
      headerName: "Status",
      field: "is_active",
      width: 130,
      cellRenderer: StatusBadge
    },
    {
      headerName: "Actions",
      field: "id",
      width: 100,
      pinned: "right",
      cellRenderer: ActionRenderer,
      sortable: false,
      filter: false
    }
  ], [StatusBadge, FinalStepBadge, ActionRenderer]);

  const defaultColDef = useMemo(() => ({
    sortable: true,
    filter: true,
    resizable: true,
    headerClass: "premium-grid-header"
  }), []);

  return (
    <Box sx={{ display: "flex", flexDirection: "column", height: "100vh", bgcolor: "#f8fafc" }}>
      <AppHeader onLogout={() => { localStorage.clear(); setIsLoggedIn(false); }} />

      <Box sx={{ p: 3, flexGrow: 1, overflow: "hidden", display: "flex", flexDirection: "column", gap: 2, maxWidth: 1400, mx: "auto", width: "100%" }}>
        
        <Fade in timeout={500}>
          <Card sx={{ p: 2, px: 3, borderRadius: 4, boxShadow: "0 4px 20px 0 rgba(0,0,0,0.04)", bgcolor: "#fff" }}>
            <Stack direction={{ xs: "column", md: "row" }} justifyContent="space-between" alignItems={{ xs: "flex-start", md: "center" }} spacing={2}>
              <Box>
                <Stack direction="row" spacing={1.5} alignItems="center">
                  <Box sx={{ p: 1, bgcolor: "#f1f5f9", borderRadius: 2.5, display: "flex" }}>
                    <WorkflowIcon sx={{ color: "primary.main", fontSize: 24 }} />
                  </Box>
                  <Typography variant="h5" sx={{ fontWeight: 800, color: "#0f172a", letterSpacing: "-0.02em" }}>
                    Approval Workflow Setup
                  </Typography>
                </Stack>
                <Typography variant="caption" sx={{ color: "#64748b", mt: 0.5, ml: 6, display: "block", fontSize: "0.85rem" }}>
                  Define multi-level approval hierarchies for system modules.
                </Typography>
              </Box>

              <Stack direction="row" spacing={2}>
                <TextField
                  size="small"
                  placeholder="Quick search..."
                  value={searchText}
                  onChange={(e) => setSearchText(e.target.value)}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon sx={{ color: "#94a3b8", fontSize: 20 }} />
                      </InputAdornment>
                    ),
                    sx: { borderRadius: 2.5, bgcolor: "#f1f5f9", border: "none", "& fieldset": { border: "none" } }
                  }}
                  sx={{ width: 320 }}
                />
                <Tooltip title="Refresh List">
                  <IconButton onClick={loadWorkflows} sx={{ bgcolor: "#f1f5f9", borderRadius: 2.5 }}><RefreshIcon /></IconButton>
                </Tooltip>
                <Button
                  variant="contained"
                  startIcon={<AddIcon />}
                  sx={{
                    bgcolor: "primary.main", borderRadius: 2.5, px: 3, fontWeight: 700, textTransform: "none",
                    boxShadow: (theme) => `0 4px 12px ${theme.palette.primary.main}33`,
                    "&:hover": { bgcolor: "primary.dark" }
                  }}
                  onClick={handleOpenAdd}
                >
                  Create Step
                </Button>
              </Stack>
            </Stack>
          </Card>
        </Fade>

        <Paper sx={{ borderRadius: 4, overflow: "hidden", boxShadow: "0 4px 20px 0 rgba(0,0,0,0.03)", flexGrow: 1, display: "flex", flexDirection: "column" }}>
          <Box className="ag-theme-quartz" sx={{ flexGrow: 1, width: "100%", "& .premium-grid-header": { fontWeight: 800, color: "#475569" } }}>
            <AgGridReact
              ref={gridRef}
              rowData={rowData}
              columnDefs={columnDefs}
              defaultColDef={defaultColDef}
              rowHeight={52}
              headerHeight={48}
              animateRows={true}
              pagination={true}
              paginationPageSize={10}
              quickFilterText={searchText}
              rowSelection={{ mode: "singleRow" }}
              suppressCellFocus={true}
              overlayNoRowsTemplate="No workflow steps configured"
            />
          </Box>
        </Paper>
      </Box>

      {/* DIALOG */}
      <Dialog open={open} onClose={() => setOpen(false)} fullWidth maxWidth="sm" TransitionComponent={Fade} transitionDuration={400}>
        <DialogTitle sx={{ fontWeight: 800, color: "#0f172a", pb: 1 }}>
          {editing ? "Modify Workflow Step" : "Define New Step"}
        </DialogTitle>

        <DialogContent>
          <Box sx={{ mt: 2, display: "flex", flexDirection: "column", gap: 2.5 }}>
            <FormControl fullWidth>
              <InputLabel>Workflow Module</InputLabel>
              <Select
                value={form.workflow_code}
                label="Workflow Module"
                onChange={(e) => setForm({ ...form, workflow_code: e.target.value })}
                sx={{ borderRadius: 2 }}
              >
                <MenuItem value="MANUAL_ATTENDANCE">MANUAL_ATTENDANCE (Attendance)</MenuItem>
                <MenuItem value="OT_REQUEST">OT_REQUEST (Overtime)</MenuItem>
                <MenuItem value="LEAVE_REQUEST">LEAVE_REQUEST (Leave)</MenuItem>
              </Select>
            </FormControl>

            <TextField
              fullWidth
              label="Sequence Step Number"
              type="number"
              placeholder="e.g. 1"
              helperText="Determines the order of approval."
              value={form.step_number}
              onChange={(e) => setForm({ ...form, step_number: parseInt(e.target.value) })}
              InputProps={{ sx: { borderRadius: 2 } }}
            />

            <FormControl fullWidth>
              <InputLabel>Responsible Approver Role</InputLabel>
              <Select
                value={form.approver_role}
                label="Responsible Approver Role"
                onChange={(e) => setForm({ ...form, approver_role: e.target.value })}
                sx={{ borderRadius: 2 }}
              >
                <MenuItem value="manager">Line Manager (L1)</MenuItem>
                <MenuItem value="hr">HR Department (L2)</MenuItem>
                <MenuItem value="gm">General Manager (GM)</MenuItem>
                <MenuItem value="director">Director Level</MenuItem>
                <MenuItem value="hr_branch">Branch HR</MenuItem>
                <MenuItem value="hr_manager">HR Manager</MenuItem>
              </Select>
            </FormControl>

            <Paper variant="outlined" sx={{ p: 1, px: 2, borderRadius: 2, bgcolor: "#f8fafc", borderStyle: "dashed" }}>
              <Stack direction="row" spacing={4}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={!!form.is_final_step}
                      onChange={(e) => setForm({ ...form, is_final_step: e.target.checked ? 1 : 0 })}
                      sx={{ "& .MuiSwitch-switchBase.Mui-checked": { color: "#db2777" }, "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": { bgcolor: "#db2777" } }}
                    />
                  }
                  label={<Typography sx={{ fontWeight: 700, fontSize: "0.85rem" }}>Final Step</Typography>}
                />

                <FormControlLabel
                  control={
                    <Switch
                      checked={!!form.is_active}
                      onChange={(e) => setForm({ ...form, is_active: e.target.checked ? 1 : 0 })}
                      sx={{ "& .MuiSwitch-switchBase.Mui-checked": { color: "#10b981" }, "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": { bgcolor: "#10b981" } }}
                    />
                  }
                  label={<Typography sx={{ fontWeight: 700, fontSize: "0.85rem" }}>Active Status</Typography>}
                />
              </Stack>
            </Paper>
          </Box>
        </DialogContent>

        <DialogActions sx={{ p: 3, pt: 1 }}>
          <Button onClick={() => setOpen(false)} sx={{ fontWeight: 700, color: "#64748b" }}>Cancel</Button>
          <Button 
            variant="contained" 
            onClick={handleSave} 
            sx={{ 
              bgcolor: "primary.main", borderRadius: 2, px: 4, fontWeight: 700,
              boxShadow: (theme) => `0 4px 12px ${theme.palette.primary.main}33`,
              "&:hover": { bgcolor: "primary.dark" }
            }}
          >
            {editing ? "Update Policy" : "Confirm Setup"}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar 
        open={snackbar.open} 
        autoHideDuration={4000} 
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
      >
        <Alert severity={snackbar.severity} variant="filled" sx={{ borderRadius: 2, fontWeight: 600 }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default ApprovalWorkflowPage;
